import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';
import { AuthBridge } from '@crewdle/auth-bridge';
import { AuthService } from '../services/auth.service';
import { AuthBridgeService } from '../services/auth-bridge.service';
import { environment } from '../../../environments/environment';

async function checkOrgMembership(): Promise<boolean> {
  const user = getAuth().currentUser;
  if (!user) return false;
  try {
    const db = getFirestore();
    const snapshot = await getDoc(doc(db, 'users', user.uid));
    const orgIds: string[] = snapshot.data()?.['organizationIds'] || [];
    return orgIds.length > 0;
  } catch {
    return true; // Don't block on transient errors
  }
}

// Refresh from Firebase so a verification done in another tab is picked up,
// then return whether the email is verified. Treats transient reload failures
// as verified so we don't lock users out on a network blip.
async function isEmailVerified(): Promise<boolean> {
  const user = getAuth().currentUser;
  if (!user) return false;
  if (user.emailVerified) return true;
  try {
    await user.reload();
  } catch {
    return true;
  }
  return user.emailVerified;
}

async function handleVerifiedSession(bridge: AuthBridge): Promise<boolean> {
  if (!(await isEmailVerified())) {
    await signOut(getAuth()).catch(() => {});
    await bridge.login();
    return false;
  }
  const hasOrg = await checkOrgMembership();
  if (hasOrg) return true;
  window.location.href = environment.adminConsoleUrl + '/new';
  return false;
}

export const authGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const bridge = inject(AuthBridgeService).bridge;
  const router = inject(Router);

  // Exchange a ?code callback (returning from centralized auth) if present.
  await bridge.ensureSession().catch(() => undefined);

  // Wait for AuthService's first auth-state resolution.
  if (auth.isLoading()) {
    await new Promise<void>((resolve) => {
      const check = setInterval(() => {
        if (!auth.isLoading()) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    });
  }

  if (auth.isAuthenticated()) {
    if (!(await handleVerifiedSession(bridge))) return false;

    // Strip the OAuth callback params from the URL. The bridge clears them via
    // history.replaceState, but Angular Router re-applies this navigation's
    // query params when it finalizes — so redirect to the clean path instead.
    if (route.queryParamMap.has('code')) {
      return router.parseUrl(state.url.split('?')[0]);
    }

    return true;
  }

  // Not signed in → hand off to centralized auth.
  await bridge.login();
  return false;
};
